import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {cls, groupArrayBy, isMobileDevice, normalizeRuStr} from '../../utils';
import {SiteRoutes} from '../../../lib/routes';
import {FilterIcon, SortIcon} from '../svg-icon';
import {Content, DropDown, Trigger} from '../drop-down';
import {SmallLoader} from '../small-loader';
import {Input} from '../inputs/input';
import {Button} from '../inputs/button';
import {List, ListController, ListGroup, ListItem, ListValue} from '../list';

import css from './sidebar.module.css';

export enum SidebarStyle {
	NORMAL = 'NORMAL',
	DISABLED = 'DISABLED',
}

export type OrderItem = {
	value: string;
	title: string;
};

type Props<T> = {
	items: T[];
	selected?: T;
	onChange: (item: T) => void;
	route: keyof SiteRoutes;
	header: (item: T) => string;
	secondary?: (item: T) => React.ReactNode;
	/** значки в правом верхнем углу строки */
	badge?: (item: T) => React.ReactNode;
	filterFn?: (item: T, filter: string) => boolean;
	style?: (item: T) => SidebarStyle | undefined | false;
	isFiltersSet?: boolean;
	onOpenFilters?: () => void;
	changeOrder?: (order: string) => void;
	order?: string;
	orderList?: OrderItem[];
	loading?: boolean;
	groupBy?: (item: T, index: number) => string | number;
	groupTitle?: (items: T[]) => string;
};

export function Sidebar<T extends {id: string}>({
	items,
	filterFn,
	selected,
	onChange,
	route,
	header,
	secondary,
	badge,
	style,
	isFiltersSet,
	onOpenFilters,
	order,
	orderList,
	changeOrder,
	loading,
	groupBy,
	groupTitle,
}: Props<T>) {
	const [filter, setFilter] = useState('');

	const filteredItems = useMemo(() => {
		if (!filter || !header) return items;
		return items.filter(
			filterFn
				? (item) => filterFn(item, filter)
				: (item) => normalizeRuStr(header(item)).includes(normalizeRuStr(filter)),
		);
	}, [filter, filterFn, items, header]);

	const groups = useMemo(() => {
		if (groupBy) return groupArrayBy(filteredItems, groupBy);
		return null;
	}, [filteredItems, groupBy]);

	const filterRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (!isMobileDevice()) filterRef.current?.focus();
	}, []);

	const listController = useRef<ListController>(null);

	const handleFilterKeyDown = useCallback((e: React.KeyboardEvent) => {
		if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
		e.preventDefault();
		listController.current?.focus();
	}, []);

	const handleNavigateOut = useCallback((edge: 'start' | 'end') => {
		if (edge === 'start') filterRef.current?.focus();
	}, []);

	const handleOrderChange = useCallback(
		(values: ListValue[]) => {
			if (changeOrder && values.length) changeOrder(String(values[0]));
		},
		[changeOrder],
	);

	const renderItem = (item: T) => (
		<ListItem
			key={item.id}
			value={item.id}
			onAction={() => onChange(item)}
			route={route}
			routeParams={{id: item.id}}
			className={css.row}
			buttonClassName={cls(css.rowButton, style?.(item) === SidebarStyle.DISABLED && css.disabled)}
		>
			<div className={cls(css.itemText, badge && css.withBadge)}>
				<div className={css.header}>{header(item)}</div>
				{secondary && <div className={css.secondary}>{secondary(item)}</div>}
			</div>
			{badge && <span className={css.badge}>{badge(item)}</span>}
		</ListItem>
	);

	return (
		<div className={css.wrapper}>
			<Input
				className={css.filter}
				setRef={filterRef}
				onKeyDown={handleFilterKeyDown}
				placeholder={'Фильтр'}
				aria-label="Фильтр по названию"
				value={filter}
				onChange={setFilter}
				appearance="filled"
				shape="circular"
				buttons={
					onOpenFilters && (
						<Button
							appearance="subtle"
							shape="circular"
							icon={<FilterIcon width={14} height={14} />}
							checked={isFiltersSet}
							onClick={onOpenFilters}
							aria-label="Фильтры"
							aria-pressed={!!isFiltersSet}
						/>
					)
				}
			/>
			<div className={css.actions}>
				{changeOrder && orderList && order && (
					<DropDown selectMode="single" value={[order]} onChange={handleOrderChange}>
						<Trigger>
							<Button appearance="subtle" icon={<SortIcon />} aria-label="Сортировка" />
						</Trigger>
						<Content align="end">
							<List aria-label="Сортировка">
								{orderList.map((item) => (
									<ListItem key={item.value} value={item.value}>
										{item.title}
									</ListItem>
								))}
							</List>
						</Content>
					</DropDown>
				)}
			</div>
			<nav className={css.scrollArea} aria-label="Список записей">
				{loading && (
					<div className={css.loading}>
						<SmallLoader />
					</div>
				)}
				{filteredItems.length > 0 && (
					<List
						virtual
						navigationMode="items"
						controllerRef={listController}
						onNavigateOut={handleNavigateOut}
						selectedItems={selected ? [selected.id] : []}
						className={css.list}
					>
						{groups
							? Object.keys(groups).map((groupKey, index) => (
									<ListGroup
										key={groupKey}
										title={groupTitle ? groupTitle(groups[groupKey]) : `Группа ${index + 1}`}
										titleClassName={css.groupTitle}
									>
										{groups[groupKey].map(renderItem)}
									</ListGroup>
							  ))
							: filteredItems.map(renderItem)}
					</List>
				)}
			</nav>
			<div className={css.counter}>Записей: {items.length}</div>
		</div>
	);
}
